import { useState, useCallback } from 'react';
import isAutoBillingAddon from '../utils/is-auto-billing-addon.ts';
import { fetchPaymentMethod, changeOrActivateSubscriptionPlan } from '../data/api';
import { sleep } from '../utils/common';
import { WEBHOOK_PROCESSING_DELAY_MS } from '../utils/constants';

/**
 * @description Custom hook to toggle auto billing mode
 * @param {{id: string, customer_id: string, status: string, meta: {baseProduct: {stripe_price_id: string}, addons: {name?: string, stripe_price_id: string}[]}}} subscription
 * @param {{id: string, stripe_token: string}} user
 * @param {string|null} autoPriceId - Stripe price ID for auto billing price
 * @param {'auto'|'manual'} mode - flag to toggle auto billing mode
 * @returns {{handleToggleAutoBilling: () => Promise<void>, status: string, error: string|null}}
 */
const useToggleAutoBilling = (subscription, user, autoPriceId, mode) => {
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);

    const handleToggleAutoBilling = useCallback(async () => {
        setStatus('pending');
        setError(null);

        if (process.env.NODE_ENV === 'test') {
            await sleep(100); // Introduce a small delay during tests
        }

        try {
            // Keep other add-ons, then include auto-billing only in automatic mode.
            const addonIds = (subscription?.meta?.addons || [])
                .filter(
                    (addon) => !isAutoBillingAddon(addon) && addon.stripe_price_id !== autoPriceId,
                )
                .map((addon) => addon.stripe_price_id);

            if (mode === 'auto') {
                addonIds.push(autoPriceId);
            }

            const data = {
                customerId: subscription.customer_id || user.stripe_token,
                subscriptionId: subscription.id,
                planId: subscription.meta.baseProduct.stripe_price_id,
                addonIds,
                isTrial: false,
                userId: user.id,
            };

            // Fetch the payment method for the user, as we need the payment method ID
            // to update the subscription
            const { paymentMethodId, error: paymentError } = await fetchPaymentMethod(data);

            if (!paymentError && paymentMethodId) {
                data.paymentMethodId = paymentMethodId;

                const billingDataResponse = await changeOrActivateSubscriptionPlan(data);

                // Ignore errors from fetching invoices, as they're not crucial to the flow and
                // can be caused by the site not being back up yet.
                if (
                    billingDataResponse?.error &&
                    billingDataResponse?.error?.message?.indexOf('Error fetching invoices') < 0
                ) {
                    setError(
                        billingDataResponse?.error?.message ||
                            'Could not update billing mode. Try again or contact support@ghost.org',
                    );
                    if (status !== 'failed') {
                        setStatus('failed');
                    }
                } else {
                    if (process.env.NODE_ENV !== 'test') {
                        // Handle success with enough delay to let Daisy and Stripe sync
                        // the changes and restart the site in the background with the new
                        // limits applied to the config.
                        await sleep(WEBHOOK_PROCESSING_DELAY_MS);
                    }

                    setStatus('succeeded');
                }
            } else {
                setError(paymentError?.message || 'Error fetching payment method');
                if (status !== 'failed') {
                    setStatus('failed');
                }
            }
        } catch (err) {
            setError(err?.message || 'Error toggling auto billing');
            if (status !== 'failed') {
                setStatus('failed');
            }
        }
    }, [subscription, user, autoPriceId, mode, status, setStatus, setError]);

    return { handleToggleAutoBilling, status, error };
};

export default useToggleAutoBilling;
